import { useCallback, useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import type { TabroomAssignment } from '@flow/core';
import { useDeps, useUseCases } from '../../app/services';
import { apiFetch } from '../../infra/api';
import { useAccount } from '../account/AccountProvider';

export interface WatcherStatus {
  status: 'disconnected' | 'connected' | 'watching' | 'expired';
  connectedAt: number | null;
  tokenExpiresAt: number | null;
  watchUntil: number | null;
  lastCheck: number | null;
  lastError: string | null;
  pushDevices: number;
  smsConfigured: boolean;
  smsTo: string | null;
  smsConsentedAt: number | null;
}

export interface WatchEvent {
  id: string;
  at: number;
  kind: 'message' | 'assignment' | 'status';
  level: 'none' | 'push' | 'sms';
  title: string;
  body: string;
  assignment?: TabroomAssignment;
  acked?: boolean;
}

/** The personal Tabroom watcher is for the owner only: admins in this app. */
export const useIsOwner = () => {
  const { state } = useAccount();
  return state.status === 'signedIn' && state.user?.role === 'admin';
};

/** Calls to the watcher API with the signed-in token. */
export const useTabroomApi = () => {
  const { getToken } = useAccount();
  return useCallback(<T,>(path: string, body?: unknown, method?: 'DELETE') =>
    apiFetch<T>(`/api/tabroom${path}`, getToken, method ? { method } : body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) }), [getToken]);
};

const SYNC_MS = 60_000;

/**
 * While flow is open (owner only): new assignments the watcher found become rounds here, then get acknowledged so
 * they're imported once. Checks about every minute and whenever the app comes back to the foreground.
 */
export const useTabroomSync = () => {
  const owner = useIsOwner();
  const call = useTabroomApi();
  const uc = useUseCases();
  const deps = useDeps();
  const busy = useRef(false);

  const sync = useCallback(async () => {
    if (busy.current || !navigator.onLine) return;
    busy.current = true;
    try {
      const { events } = await call<{ events: WatchEvent[] }>('/events');
      const todo = events.filter((e) => e.kind === 'assignment' && !e.acked && e.assignment);
      if (!todo.length) return;
      const current = (await deps.settings.get()).currentTournamentId;
      for (const e of todo) {
        const r = await uc.importAssignment(e.assignment!, current);
        notifications.show(r.ok
          ? { color: 'green', title: 'From Tabroom', message: `Added ${r.value.label}: ${r.value.teamA.code} vs ${r.value.teamB.code}${r.value.room ? `, room ${r.value.room}` : ''}.` }
          : { color: 'yellow', title: 'From Tabroom', message: `Couldn't add "${e.title}". Add it by hand.` });
      }
      await call('/events/ack', { ids: todo.map((e) => e.id) });
    } catch {
      /* offline or signed out: try again later */
    } finally {
      busy.current = false;
    }
  }, [call, uc, deps]);

  useEffect(() => {
    if (!owner) return;
    void sync();
    const id = setInterval(() => void sync(), SYNC_MS);
    const onShow = () => document.visibilityState === 'visible' && void sync();
    document.addEventListener('visibilitychange', onShow);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, [owner, sync]);
};
